import { format } from 'date-fns';
import { format as currencyFormat } from 'currency-formatter';

import { MessageBlockTextType, MessageBlockType, SlackPushPayload } from '@notifications/entities/slack-push';
import { environment as ENV } from '@environment';
import { SLACK_TRAVEL_SUPPORT_SOURCE } from '@notifications/constants/slack-hooks.constant';
import {
  RESERVATION_EXCHANGE_VALUE_LABEL, RESERVATION_FLIGHT_INFO_LABEL, SlackPushTypes,
  SlackPushTypesContext, SlackPushTypesTitle, USER_ITINERARY_INFO_LABEL
} from '@notifications/entities/push-types';
import { ExchangeBookPayload, ExchangeSearchParamsSectors, SectorTripNumber } from '@user-history/entities/user-history-item';
import { FlightItem } from '@app/entities/flight-results';
import { cabinTypeNameMapper } from '@flight-results/mappers/flights.mapper';

const DATE_FORMAT = 'dd/MM/yyyy';
const DATE_FORMAT_HOURS = 'dd/MM/yyyy HH:mm a';
const FLAG = 'flag';

export function residualExchangePushMessageMapper(
  exchangePayload: ExchangeBookPayload, exchangeData: ExchangeSearchParamsSectors, flights: FlightItem[],
  userId: string, exchangeValue: number, residualValue: number): SlackPushPayload {
  const title = ENV.production ? SlackPushTypesTitle[SlackPushTypes.ResidualExchanges] :
    SlackPushTypesTitle[SlackPushTypes.TextAlertByFront];
  return {
    type: SlackPushTypes.ResidualExchanges,
    source: SLACK_TRAVEL_SUPPORT_SOURCE,
    priority: 1,
    message: {
      blocks: [
        {
          type: MessageBlockType.Header,
          text: {
            text: title,
            type: MessageBlockTextType.Plaintext
          }
        },
        {
          type: MessageBlockType.Context,
          elements: [
            {
              text: SlackPushTypesContext[SlackPushTypes.ResidualExchanges],
              type: MessageBlockTextType.Markdown
            }
          ]
        },
        {
          type: MessageBlockType.Divider
        },
        {
          type: MessageBlockType.Section,
          text: {
            text: USER_ITINERARY_INFO_LABEL,
            type: MessageBlockTextType.Markdown
          }
        },
        {
          type: MessageBlockType.Section,
          text: {
            text: userItineraryInfoSection(exchangePayload, userId),
            type: MessageBlockTextType.Markdown
          }
        },
        {
          type: MessageBlockType.Divider
        },
        {
          type: MessageBlockType.Section,
          text: {
            text: RESERVATION_FLIGHT_INFO_LABEL,
            type: MessageBlockTextType.Markdown
          }
        },
        {
          type: MessageBlockType.Section,
          text: {
            text: flightReservationInfoSection(flights, exchangeData),
            type: MessageBlockTextType.Markdown
          }
        },
        {
          type: MessageBlockType.Divider
        },
        {
          type: MessageBlockType.Section,
          text: {
            text: RESERVATION_EXCHANGE_VALUE_LABEL,
            type: MessageBlockTextType.Markdown
          }
        },
        {
          type: MessageBlockType.Section,
          text: {
            text: flightChangeFeeSection(exchangeValue, residualValue),
            type: MessageBlockTextType.Markdown
          }
        },
        {
          type: MessageBlockType.Divider
        },
      ]
    },
  };
}

function userItineraryInfoSection(exchangePayload: ExchangeBookPayload, userId: string): string {
  // tslint:disable-next-line:prefer-template
  return `• *Country*: ${countryFlagEmoji()} \n• *Itinerary ID*: ${exchangePayload.TravelItineraryId} \n` +
    `• *User ID*: ${userId}`;
}

function flightReservationInfoSection(flights: FlightItem[], exchangeData: ExchangeSearchParamsSectors): string {
  // tslint:disable-next-line:prefer-template
  return `\n• *Original flights*: ${exchangeData.originalRoute} \n• *Original dates*: ${originalDatesFormat(exchangeData)} \n` +
    ` \n• *New dates*: ${newFlightDates(flights)} \n• *New flighs [${flightRoute(flights)}]*:\n${flightDataString(flights)}`;
}

function flightChangeFeeSection(exchangeFee: number, residualFee: number): string {
  // tslint:disable-next-line:prefer-template
  return `• *Change Value*: ${currencyLocalFormat(exchangeFee)} \n• *Residual Value*: ${currencyLocalFormat(residualFee)}`;
}

function flightRoute(flights: FlightItem[]): string {
  return `${flights[0].AllFlights[0].DepartureAirport}-${flights[0].AllFlights[flights[0].AllFlights.length - 1].ArrivalAirport}`;
}

function newFlightDates(flights: FlightItem[]): string {
  return flights.length === 1 ? formatDate(flights[0].DepartureDate, DATE_FORMAT_HOURS) : `${formatDate(flights[0].DepartureDate,
    DATE_FORMAT_HOURS)} - ${formatDate(flights[flights.length - 1].ArrivalDate, DATE_FORMAT_HOURS)}`;
}

function originalDatesFormat(exchangeData: ExchangeSearchParamsSectors): string {
  const departure = exchangeData.originalDeparture;
  const arrival = exchangeData.originalArrival;
  if (arrival && departure) {
    return `${formatDate(departure.replace(new RegExp(/-/g), '/'))} - ${formatDate(arrival.replace(new RegExp(/-/g), '/'))}`;
  } else if (departure) {
    return formatDate(departure.replace(new RegExp(/-/g), '/'));
  } else if (arrival) {
    return formatDate(arrival.replace(new RegExp(/-/g), '/'));
  } else return null;
}

function formatDate(date: string, formatString: string = DATE_FORMAT): string {
  return format(new Date(date), formatString);
}

function flightDataString(flights: FlightItem[]): string {
  let flightMarkdownData = '';
  flights.forEach(flight => {
    flightMarkdownData += `\n\t+ *${flightSectorType(flight)}*`;
    if (flight.AllFlights) {
      flight.AllFlights.map(item => {
        flightMarkdownData += `\n\t\t*Flight ${item.DepartureAirport}-${item.ArrivalAirport}*`;
        flightMarkdownData += `\n\t\t\t- Departure Date: ${formatDate(item.DepartureDate, DATE_FORMAT_HOURS)}`;
        flightMarkdownData += `\n\t\t\t- Arrival Date: ${formatDate(item.ArrivalDate, DATE_FORMAT_HOURS)}`;
        flightMarkdownData += `\n\t\t\t- Airline: ${item.AirlineName}`;
        flightMarkdownData += `\n\t\t\t- AirlineCode: ${item.AirlineCode}`;
        flightMarkdownData += `\n\t\t\t- Number: ${item.Number}`;
        flightMarkdownData += `\n\t\t\t- FareFamilies: ${item.FareFamilies}`;
        flightMarkdownData += `\n\t\t\t- Cabin: ${cabinTypeNameMapper(item.Cabin)}`;
      });
    }
  });
  return flightMarkdownData;
}

function currencyLocalFormat(price: number): string {
  const formatedPrice = currencyFormat(round(price, 2), {
    code: ENV.countryInfo.currency_code,
    precision: ENV.currency_precision,
    format: '%s%v'
  });
  return ENV.countryInfo && ENV.countryInfo.show_currency_sufix ?
    `${formatedPrice} ${ENV.countryInfo.currency_code}` : `${formatedPrice}`;
}

function round(value: number, decimalQuantity: number = 0): string | number {
  const decimal = Math.pow(10, decimalQuantity);
  return value ? Math.round(value * decimal) / decimal : value;
}

function countryFlagEmoji(): string {
  return `:${FLAG}-${ENV.countryInfo.iso_code.toLowerCase()}: ${ENV.countryInfo.iso_code}`;
}

function flightSectorType(flight: FlightItem): string {
  return flight?.SectorNumber === SectorTripNumber.DEPARTURE ? 'Departure' : 'Return';
}
